import { useCallback, useEffect, useState } from 'react'
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore/lite'
import { db } from '../lib/firebase'
import { useSesion } from './useSesion'

/**
 * Favorito de un producto puntual: si está marcado y una función para
 * alternarlo. El documento vive en `usuarios/{uid}/favoritos/{productoId}`
 * con una copia (nombre, precio, imagen) del producto en el momento de
 * marcarlo, para que listar "Mis favoritos" no tenga que volver a leer la
 * colección `productos` uno por uno.
 */
export function useFavorito(producto) {
  const { usuario } = useSesion()
  const [esFavorito, setEsFavorito] = useState(false)
  const [cargando, setCargando] = useState(Boolean(usuario))
  const [guardando, setGuardando] = useState(false)

  useEffect(() => {
    if (!usuario) {
      setEsFavorito(false)
      setCargando(false)
      return
    }

    let vigente = true
    setCargando(true)

    getDoc(doc(db, 'usuarios', usuario.uid, 'favoritos', producto.id))
      .then((snap) => {
        if (vigente) setEsFavorito(snap.exists())
      })
      .catch((err) => console.error('useFavorito:', err))
      .finally(() => {
        if (vigente) setCargando(false)
      })

    return () => {
      vigente = false
    }
  }, [usuario, producto.id])

  const alternar = useCallback(async () => {
    if (!usuario || guardando) return

    setGuardando(true)
    const ref = doc(db, 'usuarios', usuario.uid, 'favoritos', producto.id)

    try {
      if (esFavorito) {
        await deleteDoc(ref)
        setEsFavorito(false)
      } else {
        await setDoc(ref, {
          nombre: producto.nombre,
          precio: producto.precio ?? null,
          imagenUrl: producto.imagenUrl ?? null,
          agregadoEn: serverTimestamp(),
        })
        setEsFavorito(true)
      }
    } catch (err) {
      console.error('useFavorito:', err)
    } finally {
      setGuardando(false)
    }
  }, [usuario, producto, esFavorito, guardando])

  return { esFavorito, cargando, guardando, alternar }
}

/** Lista completa de favoritos del usuario, para "Mi perfil". */
export function useMisFavoritos() {
  const { usuario } = useSesion()
  const [favoritos, setFavoritos] = useState([])
  const [cargando, setCargando] = useState(true)

  useEffect(() => {
    if (!usuario) {
      setFavoritos([])
      setCargando(false)
      return
    }

    let vigente = true
    setCargando(true)

    getDocs(collection(db, 'usuarios', usuario.uid, 'favoritos'))
      .then((snapshot) => {
        if (!vigente) return
        const items = snapshot.docs
          .map((d) => ({ ...d.data(), id: d.id }))
          .sort((a, b) => (b.agregadoEn?.seconds ?? 0) - (a.agregadoEn?.seconds ?? 0))
        setFavoritos(items)
      })
      .catch((err) => console.error('useMisFavoritos:', err))
      .finally(() => {
        if (vigente) setCargando(false)
      })

    return () => {
      vigente = false
    }
  }, [usuario])

  return { favoritos, cargando }
}
